/* GlobalTabBar — Universal Raynet-style top tab bar (v4.10).
 *
 * Single horizontal strip above main content showing ALL open tabs:
 *   • Section tabs (Nástěnka / Klienti / Diář / …) — auto-added on route visit
 *   • Client tabs                                  — added via open-client-folder event
 *
 * Routing model:
 *   • Section tab click → navigate(route)        (closes client overlay)
 *   • Client tab click  → activates client overlay (dispatches focus-client-folder)
 *   • Tab close X       → removes tab; if active was closed, focus prev section
 *
 * Persists tabs in sessionStorage so refresh keeps state.
 */
import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/contexts/ProfileContext";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  X, Home, Users, Calendar, GraduationCap, Wrench, Shield, Brain, Mic, UserCheck, Settings, Mail, LogOut, ChevronDown,
  TrendingUp, Calculator, UserPlus, Briefcase, Compass, Coins, Search,
  Radar, Activity, Gift, PhoneCall, UserSearch, Handshake, Target, Trophy, CalendarClock, MapPin, FileText,
  Filter, FlaskConical, HeartPulse, PiggyBank, Scale, Stethoscope, Building2, BarChart3, Ticket, Palette, Database,
  Clock, Landmark, Umbrella, Newspaper, Megaphone,
} from "lucide-react";
import { PIPELINE_STAGES } from "@/data/crmData";
import { zapnuto } from "@/lib/funkce";
import NotificationCenter from "@/components/NotificationCenter";

const STAGE_COLOR = Object.fromEntries((PIPELINE_STAGES || []).map((s) => [s.id, s.color || "#C25934"]));

// Klávesová nápověda pro hledání — Mac ukáže ⌘K, ostatní Alt+K (Ctrl+K si bere prohlížeč).
const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");
const SEARCH_HINT = IS_MAC ? "⌘K" : "Alt+K";

/* ─── Registr sekcí = co dostane vlastní záložku ──────────────────────────────
 *
 * PRAVIDLO Č. 1 — ŽÁDNÝ ŠIROKÝ PREFIX NA SPOLEČNÉM JMENNÉM PROSTORU.
 *   Do 8/2026 tu bylo první pravidlo `p === "/" || p.startsWith("/crm")` →
 *   spolklo VŠECHNO pod /crm/* (Hypotéky, Radar příležitostí, Servis radar,
 *   Leadgen…) a poradci se v liště pořád zobrazovala jen „Nástěnka".
 *   Řešení NENÍ posunout Nástěnku na konec — široký prefix je past bez ohledu
 *   na pořadí (kdokoli přidá pravidlo pod něj, zase tiše přestane fungovat a
 *   v souboru není vidět, že na pořadí záleží). Proto Nástěnka matchuje jen
 *   PŘESNÉ cesty a každá další /crm/* obrazovka má vlastní přesné pravidlo.
 *   Nová /crm/* stránka bez pravidla nematchne NIC → spadne test pokrytí
 *   (globalTabBarRouteCoverage.test.js) → chyba se ozve hned, ne až u klienta.
 *
 * PRAVIDLO Č. 2 — prefix (startsWith) používej jen tam, kde sekce opravdu
 *   vlastní celý podstrom (/klienti, /obchody, /akademie…). Specifičtější
 *   pravidlo pak MUSÍ být výš (/nabor/cnb nad /nabor, /nastaveni/* v jedné
 *   sekci s /nastaveni). Hlídá to `samples` + test.
 *
 * `samples` = konkrétní URL, které tohle pravidlo vlastní. Test ověřuje, že
 *   findSection(sample) vrátí právě tuhle sekci → zastínění jiným pravidlem
 *   spadne v CI, ne v provozu.
 *
 * CO ZÁMĚRNĚ ZÁLOŽKU NEMÁ (nedoplňuj bez rozmyslu, je to schválně):
 *   • Veřejné stránky bez přihlášení: /login, /hd/:token, /d/:token, /m/:token,
 *     /kviz/:slug, /book/:slug, /l/:topic/:slug, /v/:slug, /w/:slug, /3d,
 *     /sila-casu/:slug, /t/:id/:slug, /privacy, /terms, /sso. Tam sedí KLIENT,
 *     ne poradce — lišta se tam ani nerenderuje (jsou mimo AppLayout).
 *   • Detail klienta /crm/clients/:cid/* (analýza, návrh, prezentace, mapa,
 *     tisk, plán). Pro klienta existuje VLASTNÍ typ záložky (type: "client",
 *     přidává se eventem global-tab-open-client a nese jméno + fázi). Sekce by
 *     vyrobila DRUHOU záložku na téhož klienta.
 *   • Mobilní shell /m/* — má vlastní navigaci (BottomNav v MobileLayout),
 *     GlobalTabBar tam vůbec není.
 *   • Přesměrování (/crm/diar → /diar apod.) — cíl záložku má, na redirectu
 *     se poradce nezastaví.
 * Pozn.: část obrazovek (kalkulačky, prezentace, hlasový režim) běží mimo
 *   AppLayout na celou plochu, takže se tam lišta nekreslí. Pravidlo tu přesto
 *   je — až je někdo do AppLayoutu obalí, záložka bude fungovat sama.
 */
const SECTION_META = [
  // ── Admin (přesné cesty; holý /admin je až za nimi) ───────────────────────
  { matches: (p) => p === "/admin/ai-knowledge",    path: "/admin/ai-knowledge",    label: "AI Knowledge",      icon: Brain,       samples: ["/admin/ai-knowledge"] },
  { matches: (p) => p === "/admin/voice-notes",     path: "/admin/voice-notes",     label: "Hlasovky",          icon: Mic,         samples: ["/admin/voice-notes"] },
  { matches: (p) => p === "/admin/email-broadcast", path: "/admin/email-broadcast", label: "Hromadný email",    icon: Mail,        samples: ["/admin/email-broadcast"] },
  { matches: (p) => p === "/admin/portfolio-doctor", path: "/admin/portfolio-doctor", label: "Portfolio (admin)", icon: Stethoscope, samples: ["/admin/portfolio-doctor"] },
  { matches: (p) => p === "/admin/trzni-data",      path: "/admin/trzni-data",      label: "Tržní data",        icon: TrendingUp,  samples: ["/admin/trzni-data"] },
  { matches: (p) => p === "/admin/mesicnik",        path: "/admin/mesicnik",        label: "Měsíčník (admin)",  icon: Newspaper,   samples: ["/admin/mesicnik"] },
  { matches: (p) => p === "/admin/data-hygiene",    path: "/admin/data-hygiene",    label: "Data hygiena",      icon: Database,    samples: ["/admin/data-hygiene"] },
  { matches: (p) => p === "/admin",                 path: "/admin",                 label: "Admin",             icon: Shield,      samples: ["/admin"] },

  // ── /crm/* — každá obrazovka přesně, ať ji Nástěnka nespolkne ─────────────
  { matches: (p) => p === "/crm/radar-prilezitosti", path: "/crm/radar-prilezitosti", label: "Radar příležitostí", icon: Radar,      samples: ["/crm/radar-prilezitosti"] },
  { matches: (p) => p === "/crm/servis-radar",       path: "/crm/servis-radar",       label: "Servis radar",       icon: Activity,   samples: ["/crm/servis-radar"] },
  { matches: (p) => p === "/crm/komunikace",        path: "/crm/komunikace",        label: "Komunikace",         icon: Mail,       samples: ["/crm/komunikace"] },
  { matches: (p) => p === "/crm/leadgen",            path: "/crm/leadgen",            label: "Doporučení",         icon: Gift,       samples: ["/crm/leadgen"] },
  { matches: (p) => p === "/crm/calls",              path: "/crm/calls",              label: "Zrcadlo volání",     icon: PhoneCall,  samples: ["/crm/calls"] },
  { matches: (p) => p === "/crm/clients",            path: "/crm/clients",            label: "Pipeline klientů",   icon: Users,      samples: ["/crm/clients"] },
  { matches: (p) => p === "/crm/leads",              path: "/crm/leads",              label: "Leady",              icon: UserSearch, samples: ["/crm/leads"] },
  { matches: (p) => p === "/crm/ik",                 path: "/crm/ik",                 label: "IK konzultace",      icon: Handshake,  samples: ["/crm/ik"] },
  { matches: (p) => p === "/crm/goals",              path: "/crm/goals",              label: "Cíle",               icon: Target,     samples: ["/crm/goals"] },
  { matches: (p) => p === "/crm/team",               path: "/crm/team",               label: "Moji poradci",       icon: UserCheck,  samples: ["/crm/team"] },
  { matches: (p) => p === "/crm/vyhlaseni",          path: "/crm/vyhlaseni",          label: "Vyhlášení",          icon: Trophy,     samples: ["/crm/vyhlaseni"] },
  { matches: (p) => p === "/crm/akce",               path: "/crm/akce",               label: "Týmové akce",        icon: CalendarClock, samples: ["/crm/akce"] },
  { matches: (p) => p === "/crm/diar-tymu",          path: "/crm/diar-tymu",          label: "Diář týmu",          icon: Calendar,   samples: ["/crm/diar-tymu"] },
  // Nástěnka = JEN rozcestník, ne celý /crm podstrom (viz pravidlo č. 1).
  { matches: (p) => p === "/" || p === "/crm",       path: "/crm",                    label: "Nástěnka",           icon: Home,       samples: ["/", "/crm"] },

  // ── Denní práce poradce ───────────────────────────────────────────────────
  { matches: (p) => p.startsWith("/klienti"),        path: "/klienti",     label: "Klienti",      icon: Users,     samples: ["/klienti"] },
  { matches: (p) => p.startsWith("/mapa-klientu"),   path: "/mapa-klientu", label: "Mapa klientů", icon: MapPin,   samples: ["/mapa-klientu"] },
  { matches: (p) => p.startsWith("/obchody"),        path: "/obchody",     label: "Obchody",      icon: Briefcase, samples: ["/obchody", "/obchody/abc123"] },
  /* JEDNA realitní záložka, ne dvě. Do 9/2026 tu byly „Reality" (přehled)
     a „Zakázky" (karta) — dvě záložky na tutéž práci, které makléři ubíraly
     místo v liště. Karta nemovitosti i karta kupujícího jsou teď obchodní
     případ (`/obchody/:id`), takže sem patří jen to, co obchodním případem
     není: přehled, zájemci a prohlídky, propočet „prodat, nebo nechat?"
     a přesměrovací adresy `/zakazky/*`. */
  { matches: (p) => p === "/reality" || p === "/crm/reality"
      || p.startsWith("/poptavky") || p.startsWith("/zakazky")
      || p.startsWith("/prodat-nebo-nechat"),
    path: "/reality", label: "Reality", icon: Building2,
    samples: ["/reality", "/crm/reality", "/poptavky/abc123", "/zakazky",
              "/zakazky/abc123/zajemci", "/prodat-nebo-nechat"] },
  { matches: (p) => p.startsWith("/rezervacni-smlouva"), path: "/rezervacni-smlouva", label: "Rezervační smlouva", icon: FileText, samples: ["/rezervacni-smlouva", "/rezervacni-smlouva/abc123"] },
  { matches: (p) => p.startsWith("/smlouvy"),        path: "/smlouvy",     label: "Smlouva",      icon: FileText,  samples: ["/smlouvy/abc123"] },
  { matches: (p) => p.startsWith("/hypoteky") || p.startsWith("/crm/hypoteky"), path: "/hypoteky", label: "Hypotéky", icon: Briefcase, samples: ["/hypoteky", "/crm/hypoteky"] },
  // Náborový radar (ČNB) je vlastní obrazovka — musí být NAD /nabor, jinak ho
  // prefix Náboru spolkne a poradci zmizí ze lišty (nahlášeno 8/2026).
  { matches: (p) => p.startsWith("/nabor/cnb"),      path: "/nabor/cnb",   label: "Náborový radar", icon: Radar,   samples: ["/nabor/cnb"] },
  { matches: (p) => p.startsWith("/nabor"),          path: "/nabor",       label: "Nábor",        icon: UserPlus,  samples: ["/nabor"] },
  { matches: (p) => p.startsWith("/diar"),           path: "/diar",        label: "Diář",         icon: Calendar,  samples: ["/diar"] },
  { matches: (p) => p.startsWith("/posta"),          path: "/posta",       label: "Pošta",        icon: Mail,      samples: ["/posta"] },
  { matches: (p) => p.startsWith("/voice-field"),    path: "/voice-field", label: "Hlasový režim", icon: Mic,      samples: ["/voice-field"] },
  { matches: (p) => p.startsWith("/manazer"),        path: "/manazer",     label: "Manažer",      icon: UserCheck, samples: ["/manazer"] },
  { matches: (p) => p === "/mesicnik",               path: "/mesicnik",    label: "Měsíčník",     icon: Newspaper, samples: ["/mesicnik"] },
  { matches: (p) => p.startsWith("/rezervace"),      path: "/rezervace",   label: "Rezervace",    icon: Ticket,    samples: ["/rezervace"] },

  // ── Vzdělávání = jedna sekce pro celou akademii i videa (čte se na pokračování) ──
  { matches: (p) => p.startsWith("/akademie") || p.startsWith("/vzdelavani") || p.startsWith("/series") || p === "/desktop",
    path: "/vzdelavani", label: "Vzdělávání", icon: GraduationCap, samples: ["/vzdelavani", "/akademie", "/akademie/produkty", "/akademie/zkouska", "/series/42", "/desktop"] },

  // ── Nástroje a kalkulačky (každý nástroj vlastní záložka jako doteď) ──────
  /* `/kalkulacka/:nastroj` (zlato / konsolidace / podpojištění) míří schválně
     do SEKCE NÁSTROJE, ne do vlastní záložky: jsou to tři karty jednoho
     rozcestníku a poradce mezi nimi na schůzce přepíná. Vlastní sekce by
     vyrobila tři skoro stejné záložky vedle sebe.
     ⚠️ Lomítko za `/kalkulacka` je nutné — bez něj by prefix spolkl i
     `/kalkulacka-vizual`, který má vlastní sekci níž. */
  { matches: (p) => p.startsWith("/tools") || p.startsWith("/nastroje") || p.startsWith("/calculators") || p === "/kalkulacka" || p.startsWith("/kalkulacka/") || p.startsWith("/kalkulacky/"), path: "/tools", label: "Nástroje", icon: Wrench, samples: ["/tools", "/nastroje", "/calculators", "/kalkulacka/zlato", "/kalkulacky/hypoteka"] },
  { matches: (p) => p.startsWith("/trychtyre"),          path: "/trychtyre",          label: "Trychtýře",          icon: Filter,      samples: ["/trychtyre"] },
  { matches: (p) => p.startsWith("/historicka") || p === "/historical", path: "/historicka-kalkulacka", label: "Historická", icon: TrendingUp, samples: ["/historicka-kalkulacka", "/historical"] },
  { matches: (p) => p.startsWith("/fin-matematika"),     path: "/fin-matematika",     label: "Fin. matematika",    icon: Calculator,  samples: ["/fin-matematika"] },
  { matches: (p) => p.startsWith("/investicni-studio"),  path: "/investicni-studio",  label: "Investiční studio",  icon: FlaskConical, samples: ["/investicni-studio"] },
  { matches: (p) => p.startsWith("/investicni-strategie"), path: "/investicni-strategie", label: "Strategie",     icon: Compass,     samples: ["/investicni-strategie"] },
  { matches: (p) => p.startsWith("/zivotni-pojisteni"),  path: "/zivotni-pojisteni",  label: "Životní pojištění",  icon: HeartPulse,  samples: ["/zivotni-pojisteni"] },
  { matches: (p) => p.startsWith("/penze"),              path: "/penze",              label: "Penze",              icon: PiggyBank,   samples: ["/penze"] },
  /* Analýza pro Vietnamce (dřív „Proč pojistka"). Přesná shoda, ne prefix — viz PRAVIDLO Č. 2 výš. */
  { matches: (p) => p === "/analyza-vietnam" || p.startsWith("/analyza-vietnam/"), path: "/analyza-vietnam", label: "Analýza pro Vietnamce", icon: Umbrella, samples: ["/analyza-vietnam"] },
  { matches: (p) => p.startsWith("/renta-porovnavac"),   path: "/renta-porovnavac",   label: "Renta — porovnávač", icon: Scale,       samples: ["/renta-porovnavac"] },
  { matches: (p) => p.startsWith("/fixace") || p.startsWith("/hypotecni-fixace"), path: "/fixace", label: "Fixace pod kontrolou", icon: Landmark, samples: ["/fixace", "/hypotecni-fixace"] },
  { matches: (p) => p.startsWith("/svoboda"),             path: "/svoboda",             label: "Kdy budeš volný",   icon: Clock,       samples: ["/svoboda"] },
  { matches: (p) => p.startsWith("/hypoteka-rozhodnuti"), path: "/hypoteka-rozhodnuti", label: "Hypotéka — rozhodnutí", icon: Building2, samples: ["/hypoteka-rozhodnuti"] },
  { matches: (p) => p.startsWith("/srovnavac-fondu"),    path: "/srovnavac-fondu",    label: "Srovnávač fondů",    icon: Scale,       samples: ["/srovnavac-fondu"] },
  { matches: (p) => p.startsWith("/portfolio-doktor"),   path: "/portfolio-doktor",   label: "Portfolio doktor",   icon: Stethoscope, samples: ["/portfolio-doktor"] },
  { matches: (p) => p.startsWith("/nemovitost-pronajem"), path: "/nemovitost-pronajem", label: "Nemovitost",       icon: Building2,   samples: ["/nemovitost-pronajem"] },
  { matches: (p) => p.startsWith("/kalkulacka-vizual"),  path: "/kalkulacka-vizual",  label: "Kalkulačka vizuál",  icon: BarChart3,   samples: ["/kalkulacka-vizual"] },
  { matches: (p) => p.startsWith("/provize"),            path: "/provize",            label: "Provize",            icon: Coins,       samples: ["/provize"] },
  { matches: (p) => p.startsWith("/design-lab"),         path: "/design-lab",         label: "Design Lab",         icon: Palette,     samples: ["/design-lab/analyza-2"] },
  { matches: (p) => p === "/novinky",                     path: "/novinky",            label: "Co je nového",       icon: Megaphone,   samples: ["/novinky"] },

  // Nastavení = jedna sekce (web i vizitka jsou její podstránky).
  { matches: (p) => p.startsWith("/settings") || p.startsWith("/nastaveni"), path: "/settings", label: "Nastavení", icon: Settings, samples: ["/settings", "/nastaveni", "/nastaveni/web", "/nastaveni/vizitka"] },
];

/* SLOVENSKÉ NÁZVY ZÁLOŽEK — historický zdroj, dnes už NE překladová cesta.
 * Do 17. 8. 2026 se tímhle overlayem překládalo při renderu, jenže uměl přesně
 * dva jazyky → vietnamec i ukrajinec viděl české záložky. Render teď jede přes
 * t() a TAB_KLIC (níž). Tahle mapa zůstává jako doklad slovenštiny pro test
 * globalTabBarRouteCoverage.test.js, který zároveň hlídá, že se NEROZEJDE se
 * slovníkem sk.js. Když přidáš sekci, přidej ji sem i do TAB_KLIC + slovníků. */
const SK_TAB = {
  "Nástěnka": "Nástenka",
  "Klienti": "Klienti",
  "Pipeline klientů": "Pipeline klientov",
  "Zrcadlo volání": "Zrkadlo volaní",
  "Leady": "Leady",
  "Radar příležitostí": "Radar príležitostí",
  "Servis radar": "Servisný radar",
  "Komunikace": "Komunikácia",
  "Doporučení": "Odporúčania",
  "Mapa klientů": "Mapa klientov",
  "Cíle": "Ciele",
  "IK konzultace": "IK konzultácie",
  "Moji poradci": "Moji poradcovia",
  "Vyhlášení": "Vyhlásenie",
  "Týmové akce": "Tímové akcie",
  "Diář týmu": "Diár tímu",
  "Diář": "Diár",
  "Pošta": "Pošta",
  "Manažer": "Manažér",
  "Obchody": "Obchody",
  "Reality": "Reality",
  "Zakázky": "Zákazky",
  "Rezervační smlouva": "Rezervačná zmluva",
  "Smlouva": "Zmluva",
  "Hypotéky": "Hypotéky",
  "Nábor": "Nábor",
  "Náborový radar": "Náborový radar",
  "Hlasový režim": "Hlasový režim",
  "Rezervace": "Rezervácie",
  "Vzdělávání": "Vzdelávanie",
  "Nástroje": "Nástroje",
  "Trychtýře": "Lieviky",
  "Historická": "Historická",
  "Fin. matematika": "Fin. matematika",
  "Investiční studio": "Investičné štúdio",
  "Strategie": "Stratégie",
  "Životní pojištění": "Životné poistenie",
  "Penze": "Dôchodok",
  "Analýza pro Vietnamce": "Analýza pre Vietnamcov",
  "Renta — porovnávač": "Renta — porovnávač",
  "Fixace pod kontrolou": "Fixácia pod kontrolou",
  "Kdy budeš volný": "Kedy budeš voľný",
  "Hypotéka — rozhodnutí": "Hypotéka — rozhodnutia",
  "Srovnávač fondů": "Porovnávač fondov",
  "Portfolio doktor": "Portfólio doktor",
  "Nemovitost": "Nehnuteľnosť",
  "Kalkulačka vizuál": "Kalkulačka vizuál",
  "Provize": "Provízie",
  "Design Lab": "Design Lab",
  "Admin": "Admin",
  "AI Knowledge": "AI Knowledge",
  "Hlasovky": "Hlasovky",
  "Hromadný email": "Hromadný e-mail",
  "Portfolio (admin)": "Portfólio (admin)",
  "Tržní data": "Trhové dáta", "Měsíčník (admin)": "Mesačník (admin)", "Měsíčník": "Mesačník",
  "Data hygiena": "Hygiena dát",
  "Nastavení": "Nastavenia", "Co je nového": "Čo je nové",
};
/* český label záložky → klíč do slovníku `lista.*` (data/translations/*.js).
 * 19 klíčů sdílíme s levou lištou (stejný text = stejný překlad), zbylých 28
 * má vlastní `tab*` klíč. */
const TAB_KLIC = {
  "Nástěnka": "nastenka", "Klienti": "klienti", "Pipeline klientů": "pipelineKlientu",
  "Zrcadlo volání": "zrcadloVolani", "Leady": "tabLeady", "Radar příležitostí": "radarPrilezitosti",
  "Servis radar": "tabServisRadar", "Komunikace": "tabKomunikace", "Doporučení": "tabDoporuceni", "Mapa klientů": "mapaKlientu",
  "Cíle": "tabCile", "IK konzultace": "ikKonzultace", "Moji poradci": "mojiPoradci",
  "Vyhlášení": "tabVyhlaseni", "Týmové akce": "tymoveAkce", "Diář týmu": "diarTymu",
  "Diář": "diar", "Pošta": "posta", "Manažer": "manazer", "Obchody": "obchody",
  "Reality": "tabReality",
  "Zakázky": "tabZakazky", "Rezervační smlouva": "tabRezervacniSmlouva",
  "Smlouva": "tabSmlouva", "Hypotéky": "tabHypoteky", "Nábor": "tabNabor",
  "Náborový radar": "naborovyRadar", "Hlasový režim": "tabHlasovyRezim",
  "Rezervace": "tabRezervace", "Vzdělávání": "vzdelavani", "Nástroje": "nastroje",
  "Trychtýře": "tabTrychtyre", "Historická": "tabHistoricka", "Fin. matematika": "tabFinMatematika",
  "Investiční studio": "tabInvesticniStudio", "Strategie": "tabStrategie",
  "Životní pojištění": "zivotniPojisteni", "Penze": "tabPenze", "Analýza pro Vietnamce": "tabIzp",
  "Renta — porovnávač": "tabRentaPorovnavac",
  "Fixace pod kontrolou": "tabFixace", "Kdy budeš volný": "tabSvoboda", "Hypotéka — rozhodnutí": "tabHypotekaRozhodnuti",
  "Srovnávač fondů": "srovnavacFondu", "Portfolio doktor": "tabPortfolioDoktor",
  "Nemovitost": "tabNemovitost", "Kalkulačka vizuál": "tabKalkulackaVizual",
  "Provize": "tabProvize", "Design Lab": "tabDesignLab", "Admin": "tabAdmin",
  "AI Knowledge": "tabAiKnowledge", "Hlasovky": "tabHlasovky",
  "Hromadný email": "tabHromadnyEmail", "Portfolio (admin)": "tabPortfolioAdmin",
  "Tržní data": "tabTrzniData", "Měsíčník (admin)": "tabMesicnikAdmin", "Měsíčník": "tabMesicnik",
  "Data hygiena": "tabDataHygiena", "Nastavení": "tabNastaveni", "Co je nového": "tabNovinky",
};

/** Název záložky v jazyce uživatele. Fallback = původní český text. */
const tabT = (s, t) => (TAB_KLIC[s] ? t(`lista.${TAB_KLIC[s]}`, s) : s);

// Cestu normalizuj (pryč query/hash a koncové lomítko), ať /crm/ i /crm?x=1
// spadnou do stejné sekce jako /crm.
const normalizePath = (pathname) => {
  if (typeof pathname !== "string" || !pathname) return "/";
  const clean = pathname.split("?")[0].split("#")[0];
  if (clean.length <= 1) return clean || "/";
  return clean.replace(/\/+$/, "") || "/";
};
const findSection = (pathname) => {
  const p = normalizePath(pathname);
  return SECTION_META.find((s) => s.matches(p));
};
// Kam vede klik na sekční záložku: na poslední navštívenou podstránku sekce,
// jinak na její kořen (starší záložky ze sessionStorage lastPath nemají).
const sectionTarget = (tab) => tab?.lastPath || tab?.path;
const initials = (name = "", surname = "") => `${(name || "").charAt(0)}${(surname || "").charAt(0)}`.toUpperCase() || "?";

const STORAGE_KEY = "global-tabs-v1";

// Export jen pro testy (globalTabBarRouteCoverage.test.js) — hlídají, že každá
// routa z App.js má sekci a že se pravidla navzájem nezastiňují.
export { SECTION_META, SK_TAB, TAB_KLIC, findSection, normalizePath };

/* Záložky sekcí, jejichž funkce je VYPNUTÁ přepínačem (lib/funkce.js).
   Kdo měl sekci otevřenou ještě před vypnutím, nesmí ji v liště najít dál —
   klik by ho stejně jen přesměroval jinam. Sekce v SECTION_META zůstává
   (test pokrytí rout ji potřebuje a po zapnutí se hodí). Ptá se při načtení
   záložek, ne při načtení modulu, aby šel přepínač v testu namockovat. */
const ZALOZKY_ZA_PREPINACEM = { "section:/rezervacni-smlouva": "rezervacniSmlouva", "section:/voice-field": "aiTlacitka" };
const zalozkaPovolena = (tab) => !ZALOZKY_ZA_PREPINACEM[tab.id] || zapnuto(ZALOZKY_ZA_PREPINACEM[tab.id]);

export function loadTabs() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    // Validní JSON, který NENÍ pole (poškozené úložiště, {}/číslo/string) by shodilo
    // tabs.map při renderu. Vynutit pole + jen položky s id (jinak render padne na undefined).
    return Array.isArray(parsed) ? parsed.filter((t) => t && t.id && zalozkaPovolena(t)) : [];
  } catch { return []; }
}
function saveTabs(tabs) {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tabs)); } catch { /* noop */ }
}

export default function GlobalTabBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  // Tabs: { id, type: 'section'|'client', label, path?, contactId?, name?, surname?, stage? }
  const [tabs, setTabs] = useState(loadTabs);
  // activeId: which tab is "on top" — for clients it determines which folder shows
  const [activeId, setActiveId] = useState(null);

  /* Zrcadla stavu v refech. PROČ: dřív se vedlejší efekty (window.dispatchEvent,
     vnořený setActiveId) volaly UVNITŘ updateru `setTabs(prev => …)`. React updater
     může spustit během renderu → konzole hlásila „Cannot update a component
     (ClientFolderHost) while rendering a different component (GlobalTabBar)"
     (nahlásil testovací agent 21. 9. 2026). Z refů si stav přečteme dopředu,
     updatery zůstanou čisté a efekty běží až po nich. */
  const tabsRef = useRef(tabs);
  const activeIdRef = useRef(activeId);
  useEffect(() => { tabsRef.current = tabs; }, [tabs]);
  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);

  // ─── Auto-add section tab on route change ───────────────────────
  // `lastPath` = konkrétní URL, kde poradce v té sekci naposledy byl (i s query).
  // Bez toho klik na záložku skočil na kořen sekce a rozdělaná práce zmizela —
  // přesně to, co majitel hlásil („zmizí mi to a musím to zadat znovu").
  useEffect(() => {
    const section = findSection(location.pathname);
    if (!section) return;
    const id = `section:${section.path}`;
    const here = `${location.pathname}${location.search || ""}`;
    setTabs((prev) => {
      const idx = prev.findIndex((t) => t.id === id);
      if (idx < 0) return [...prev, { id, type: "section", label: section.label, path: section.path, lastPath: here }];
      if (prev[idx].lastPath === here) return prev; // nic nového → žádný re-render
      const next = [...prev];
      next[idx] = { ...next[idx], lastPath: here };
      return next;
    });
    // Activate this section unless a client overlay is currently in front
    setActiveId((cur) => {
      const isClientActive = cur && cur.startsWith("client:");
      return isClientActive ? cur : id;
    });
  }, [location.pathname, location.search]);

  // ─── Listen for client open/close events ────────────────────────
  useEffect(() => {
    const onOpen = (e) => {
      const contactId = e.detail?.contactId;
      const meta = e.detail?.meta || {}; // { name, surname, stage }
      if (!contactId) return;
      const id = `client:${contactId}`;
      setTabs((prev) => {
        const idx = prev.findIndex((t) => t.id === id);
        if (idx >= 0) {
          // Update meta if newer info available
          const updated = [...prev];
          updated[idx] = { ...updated[idx], ...meta };
          return updated;
        }
        return [...prev, { id, type: "client", contactId, ...meta }];
      });
      setActiveId(id);
    };
    const onUpdateMeta = (e) => {
      const { contactId, meta } = e.detail || {};
      if (!contactId || !meta) return;
      setTabs((prev) => prev.map((t) => (t.id === `client:${contactId}` ? { ...t, ...meta } : t)));
    };
    const onClose = (e) => {
      const contactId = e.detail?.contactId;
      if (!contactId) return;
      // Mirror the close via the same path as user-clicking X
      const id = `client:${contactId}`;
      const prev = tabsRef.current;
      const idx = prev.findIndex((t) => t.id === id);
      if (idx < 0) return;
      const next = [...prev.slice(0, idx), ...prev.slice(idx + 1)];
      setTabs(next);
      // If we closed the active, fall back to neighbor
      if (activeIdRef.current === id) {
        const fallback = next[idx - 1] || next[idx] || next[0] || null;
        if (fallback) {
          setActiveId(fallback.id);
          if (fallback.type === "section") {
            window.dispatchEvent(new CustomEvent("global-tab-section-activated"));
            navigate(sectionTarget(fallback));
          } else {
            window.dispatchEvent(new CustomEvent("open-client-folder", { detail: { contactId: fallback.contactId } }));
          }
        } else {
          setActiveId(null);
          window.dispatchEvent(new CustomEvent("global-tab-section-activated"));
        }
      }
      // Forget cache in ClientFolderHost
      window.dispatchEvent(new CustomEvent("global-tab-internal-close-client", { detail: { contactId } }));
    };
    const onCloseAll = () => {
      // Remove client tabs only — section tabs stay
      const prev = tabsRef.current;
      setTabs(prev.filter((t) => t.type !== "client"));
      prev.filter((t) => t.type === "client").forEach((t) => {
        window.dispatchEvent(new CustomEvent("global-tab-internal-close-client", { detail: { contactId: t.contactId } }));
      });
      if (activeIdRef.current && activeIdRef.current.startsWith("client:")) {
        setActiveId(null);
        window.dispatchEvent(new CustomEvent("global-tab-section-activated"));
      }
    };
    window.addEventListener("global-tab-open-client", onOpen);
    window.addEventListener("global-tab-update-client-meta", onUpdateMeta);
    window.addEventListener("global-tab-close-client", onClose);
    window.addEventListener("close-all-client-folders", onCloseAll);
    return () => {
      window.removeEventListener("global-tab-open-client", onOpen);
      window.removeEventListener("global-tab-update-client-meta", onUpdateMeta);
      window.removeEventListener("global-tab-close-client", onClose);
      window.removeEventListener("close-all-client-folders", onCloseAll);
    };
  }, []); // eslint-disable-line

  // ─── Persist tabs ───────────────────────────────────────────────
  const initial = useRef(true);
  useEffect(() => {
    if (initial.current) { initial.current = false; return; }
    saveTabs(tabs);
  }, [tabs]);

  // Pozn.: druhý efekt „mark active", který dělal totéž co ten výše, byl smazán —
  // dvě kopie stejné logiky se dřív nebo později rozejdou.

  // ─── Tab handlers ───────────────────────────────────────────────
  const activateTab = (tab) => {
    if (tab.type === "section") {
      // Hide client overlay (but keep client tabs in the bar)
      window.dispatchEvent(new CustomEvent("global-tab-section-activated"));
      navigate(sectionTarget(tab));
      setActiveId(tab.id);
    } else {
      // Client tab — open folder overlay
      window.dispatchEvent(new CustomEvent("open-client-folder", { detail: { contactId: tab.contactId } }));
      setActiveId(tab.id);
    }
  };

  const closeTab = (id) => {
    // Vedlejší efekty ZÁMĚRNĚ mimo updater `setTabs` — viz komentář u tabsRef.
    const prev = tabsRef.current;
    const idx = prev.findIndex((t) => t.id === id);
    if (idx < 0) return;
    const next = [...prev.slice(0, idx), ...prev.slice(idx + 1)];
    const closed = prev[idx];
    setTabs(next);
    // If we closed the active, focus a neighbor
    if (activeIdRef.current === id) {
      const fallback = next[idx - 1] || next[idx] || next[0] || null;
      if (fallback) {
        setTimeout(() => activateTab(fallback), 0);
      } else {
        setActiveId(null);
        window.dispatchEvent(new CustomEvent("global-tab-section-activated"));
      }
    }
    // If client tab → ask ClientFolderHost to forget cache
    if (closed?.type === "client") {
      window.dispatchEvent(new CustomEvent("global-tab-internal-close-client", { detail: { contactId: closed.contactId } }));
    }
  };

  const closeAllTabs = () => {
    tabs.forEach((t) => { if (t.type === "client") window.dispatchEvent(new CustomEvent("global-tab-internal-close-client", { detail: { contactId: t.contactId } })); });
    setTabs([]);
    setActiveId(null);
    window.dispatchEvent(new CustomEvent("global-tab-section-activated"));
  };

  if (tabs.length === 0) return null;

  return (
    // POZOR: overflow-x-auto smí mít JEN vnitřní pás záložek, ne celý bar —
    // jinak ořízne absolutní dropdowny (UserMenu se otevřel, ale nebyl vidět)
    // a při mnoha záložkách odscrollovaly i ovládací prvky vpravo.
    <div
      className="ink-board flex items-end gap-1 px-3 pt-1.5 flex-shrink-0 sticky top-0 z-[80] pl-[60px] lg:pl-3" /* nad ClientFolderHost (z-78); na mobilu levý padding pod hamburgerem (fixed vlevo nahoře) */
      style={{
        background: "color-mix(in srgb, var(--bg) 94%, transparent)",
        backdropFilter: "blur(8px)",
        borderBottom: "1px solid var(--line)",
        minHeight: 42,
      }}
      data-testid="global-tab-bar"
    >
      <div
        className="flex items-end gap-1 overflow-x-auto min-w-0 flex-1"
        style={{ WebkitOverflowScrolling: "touch", overscrollBehavior: "contain", scrollbarWidth: "none", msOverflowStyle: "none" }}
        data-testid="global-tab-strip"
      >
        <AnimatePresence initial={false}>
          {tabs.map((t) => (
            <TabPill
              key={t.id}
              tab={t}
              active={t.id === activeId}
              onClick={() => activateTab(t)}
              onClose={() => closeTab(t.id)}
            />
          ))}
        </AnimatePresence>
      </div>

      {/* Right-aligned: lupa + zavřít vše + účet (jazyk a vzhled jsou v Nastavení) */}
      <div className="flex items-center gap-2 pb-1 pr-1 flex-shrink-0">
        {/* lg:hidden — na desktopu je Hledat (⌘K) už v hlavičce levé lišty; dvě stejná
            tlačítka na jedné obrazovce = zmatek (velký test funkčnosti 24. 10. 2026).
            Na mobilu je lišta schovaná v draweru, tak tady zůstává. */}
        <button onClick={() => window.dispatchEvent(new CustomEvent("open-global-search"))} data-testid="open-search-btn"
          title={`${t("auto.globalTabBar.hledat", 'Hledat')} (${SEARCH_HINT})`} className="inline-flex lg:hidden items-center gap-1.5 text-drobny font-medium px-2.5 py-1 rounded-full whitespace-nowrap"
          style={{ color: "var(--muted)", background: "var(--surface)", border: "1px solid var(--line)", boxShadow: "var(--shadow-card, 0 2px 10px rgba(38,34,26,0.05))" }}>
          <Search size={12} /> <span className="hidden sm:inline">{t("auto.globalTabBar.hledat", 'Hledat')}</span>
          <kbd className="hidden md:inline-flex items-center text-drobny font-bold px-1.5 py-0.5 rounded leading-none tabular-nums"
            style={{ background: "var(--bg)", border: "1px solid var(--line)", color: "var(--muted)" }}>{SEARCH_HINT}</kbd>
        </button>
        {tabs.length > 1 && (
          <button onClick={closeAllTabs} data-testid="close-all-tabs" title={t("auto.globalTabBar.zavritVsechnyZalozky", 'Zavřít všechny záložky')}
            className="inline-flex items-center gap-1 text-drobny font-medium px-2.5 py-1 rounded-full whitespace-nowrap"
            style={{ color: "var(--muted)", background: "var(--surface)", border: "1px solid var(--line)", boxShadow: "var(--shadow-card, 0 2px 10px rgba(38,34,26,0.05))" }}>
            <X size={11} /> {t("auto.globalTabBar.zavritVse", 'Zavřít vše')}
          </button>
        )}
        <NotificationCenter />
        <UserMenu />
      </div>
    </div>
  );
}

/* Uživatelské menu vpravo nahoře (jako Raynet) — avatar → Nastavení / Odhlásit. */
function UserMenu() {
  const { user, logout } = useAuth();
  const { profile } = useProfile();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const initial = (user?.name?.[0] || "?").toUpperCase();
  const photo = profile?.photo_base64 || ""; // profilová fotka poradce (nastaví se v Nastavení)
  const Avatar = ({ size }) => (
    photo
      ? <img src={photo} alt={user?.name || "Účet"} className="rounded-full object-cover flex-shrink-0" style={{ width: size, height: size }} />
      : <span className="rounded-full flex items-center justify-center font-bold flex-shrink-0" style={{ width: size, height: size, fontSize: size * 0.42, background: "var(--accent-soft)", color: "var(--accent-ink)" }}>{initial}</span>
  );
  const itemStyle = { color: "var(--ink)" };
  const onEnter = (e) => (e.currentTarget.style.background = "var(--accent-soft)");
  const onLeave = (e) => (e.currentTarget.style.background = "transparent");
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen((v) => !v)} data-testid="topbar-user-menu" title={t("lista.ucet", "Účet")}
        className="inline-flex items-center gap-1.5 pl-1 pr-2 py-1 rounded-full"
        style={{ background: "var(--surface)", border: "1px solid var(--line)", boxShadow: "var(--shadow-card, 0 2px 10px rgba(38,34,26,0.05))" }}>
        <Avatar size={24} />
        <span className="hidden md:inline text-drobny font-semibold max-w-[120px] truncate" style={{ color: "var(--ink)" }}>{user?.name || "Účet"}</span>
        <ChevronDown size={12} style={{ color: "var(--muted)" }} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1.5 py-1.5 rounded-xl z-[130]"
          style={{ minWidth: 210, background: "var(--surface)", border: "1px solid var(--line)", boxShadow: "var(--shadow-float, 0 24px 60px rgba(38,34,26,0.3))" }}
          data-testid="topbar-user-dropdown">
          <div className="px-3 py-2 border-b flex items-center gap-2.5" style={{ borderColor: "var(--line)" }}>
            <Avatar size={34} />
            <div className="min-w-0">
              <div className="text-maly font-semibold truncate" style={{ color: "var(--ink)" }}>{user?.name || t("crm.advisorWord", 'Poradce')}</div>
              <div className="text-drobny truncate" style={{ color: "var(--muted)" }}>{user?.email}</div>
            </div>
          </div>
          <button onClick={() => { setOpen(false); navigate("/settings"); }} data-testid="topbar-menu-settings"
            className="w-full flex items-center gap-2.5 px-3 py-2 text-maly text-left" style={itemStyle} onMouseEnter={onEnter} onMouseLeave={onLeave}>
            <Settings size={14} /> {t("auto.globalTabBar.nastaveni", 'Nastavení')}
          </button>
          <button onClick={async () => { setOpen(false); await logout(); navigate("/login"); }} data-testid="topbar-menu-logout"
            className="w-full flex items-center gap-2.5 px-3 py-2 text-maly text-left" style={itemStyle} onMouseEnter={onEnter} onMouseLeave={onLeave}>
            <LogOut size={14} /> {t("auto.globalTabBar.odhlasit", 'Odhlásit')}
          </button>
        </div>
      )}
    </div>
  );
}

function TabPill({ tab, active, onClick, onClose }) {
  const { t } = useLanguage();
  const isSection = tab.type === "section";
  const SectionIcon = isSection
    ? (SECTION_META.find((s) => s.path === tab.path)?.icon || Home)
    : null;
  const stageColor = isSection ? null : (STAGE_COLOR[tab.stage] || "#C25934");
  const label = isSection
    ? tabT(tab.label, t)
    : (`${tab.name || ""} ${tab.surname || ""}`.trim() || "Klient");
  const zavritPopis = t("denniCrm.zavritZalozku", "Zavřít záložku {nazev}", { nazev: label });

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 4, scale: 0.96 }}
      transition={{ type: "spring", bounce: 0.18, duration: 0.28 }}
      onClick={onClick}
      className="relative flex items-center gap-1.5 px-2.5 py-1.5 cursor-pointer flex-shrink-0 select-none group"
      style={{
        /* Plocha a obrys jdou přes tokeny, ne napevno bíle — na tmavém motivu
           (Midnight) svítila aktivní záložka jako bílý obdélník uprostřed tmavé
           lišty. Neaktivní je poloprůhledná plocha, takže drží v obou režimech. */
        background: active ? "var(--surface)" : "var(--surface-2)",
        border: `1px solid ${active ? "var(--line)" : "var(--line)"}`,
        borderBottomColor: active ? "transparent" : "var(--line)",
        borderTopLeftRadius: 9,
        borderTopRightRadius: 9,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        marginBottom: -1,
        color: active ? "var(--ink)" : "var(--muted)",
        boxShadow: active ? "0 -1px 4px rgba(38,34,26,0.05)" : "none",
        minWidth: isSection ? 100 : 130,
        maxWidth: 220,
      }}
      data-testid={`global-tab-${tab.id}`}
    >
      {/* Section icon OR client stage dot */}
      {isSection ? (
        <SectionIcon size={12} style={{ color: active ? "var(--accent)" : "var(--muted)" }} />
      ) : (
        <>
          <span className="flex-shrink-0" style={{ width: 7, height: 7, background: stageColor, borderRadius: 2 }} />
          <span
            className="flex-shrink-0 flex items-center justify-center rounded-full text-drobny font-bold"
            style={{
              width: 20, height: 20,
              background: active ? "var(--accent-soft)" : "rgba(38,34,26,0.06)",
              color: active ? "var(--accent-ink)" : "var(--muted)",
            }}
          >
            {initials(tab.name, tab.surname)}
          </span>
        </>
      )}

      <span className="text-drobny font-medium truncate flex-1 min-w-0" title={label}>
        {label}
      </span>

      {/* Zavřít záložku. Klikací plocha 24 × 24 px (WCAG 2.5.8; dřív 16 px), záporný
          okraj ji „schová“, takže záložka nezvýší. Jméno pro čtečku i hlas: „Zavřít
          záložku Jan Novák“. Barvy jen z tokenů — dřív natvrdo #8C3B2E, v modrém
          a tmavém motivu cizí červená. */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        data-testid={`global-tab-close-${tab.id}`}
        aria-label={zavritPopis}
        title={zavritPopis}
        className="flex-shrink-0 w-6 h-6 -my-1 -mr-1.5 rounded-full flex items-center justify-center transition-colors text-ink-faint hover:bg-accent-soft hover:text-accent-ink"
      >
        <X size={12} aria-hidden />
      </button>

      {active && (
        <motion.span
          layoutId="global-tab-active-line"
          className="absolute left-1.5 right-1.5"
          style={{ bottom: -1, height: 2, background: "var(--accent)", borderRadius: 2 }}
        />
      )}
    </motion.div>
  );
}
